import axios from "axios";
import { compareAsc, format, parse } from "date-fns/esm";

import { auth } from "@/auth";
import store from "@/store/root";
import { Item } from "@/tools/msa/models/item";
import { Document, Session } from "@/tools/msa/models";
import { Response } from "@/tools/msa/models/requests/snomed";

export const encodeDocumentWithSnomed = async (
  document: Document,
  session: Session
): Promise<Document> => {
  const sections = document.pages.flatMap(page => page.sections);

  await axios
    .post<Response>(
      (store.state as any).settings.current.endpoints.msa,
      {
        document: {
          dob: session.patient.dateOfBirth
            ? format(session.patient.dateOfBirth, "y-M-d")
            : "",
          pages: sections.map(section => ({
            date:
              section.page.date || document.date
                ? format(
                    parse(
                      (section.page.date || document.date)!,
                      "M/d/y",
                      new Date()
                    ),
                    "y-M-d"
                  )
                : "",
            text: section.tokens
              .map(token => `${token.text}${token.delimiter}`)
              .join(""),
            action:
              section.included === null
                ? "null"
                : section.included
                ? "include"
                : "exclude"
          }))
        }
      },
      { headers: { ...(await auth.headers()), "X-Session-Id": session.id } }
    )
    .then(response => response.data)
    .then(data => {
      sections.forEach(
        (section, index) =>
          (section.included = !data.pages_excluded.includes(index + 1))
      );
      const tokens = sections
        .filter(section => section.included)
        .flatMap(section => section.tokens);

      document.items = [
        ...document.items.filter(item => item.type !== "code_match"),
        ...data.icd.imatches
          .filter(match => Object.keys(match).length > 0)
          .map(
            (match): Item => ({
              type: "code_match",
              document,
              match: {
                coding: Object.freeze({
                  type: match.codetype,
                  system: match.codeset,
                  code: match.code,
                  display: match.codename
                }),
                refers: {
                  dates: match.refers.dates
                    .filter(Boolean)
                    .filter(date =>
                      /[0-9]{1,2}-[0-9]{1,2}-[0-9]{1,4}/.test(date)
                    )
                    .map(date => parse(date, "y-M-d", new Date()))
                    .sort(compareAsc)
                    .map(date => format(date, "M/d/y"))
                },
                locations: match.tokspans
                  .filter(span => span.length === 2)
                  .map(span => tokens.slice(...span))
              }
            })
          )
      ];
    });

  return document;
};
